import * as CryptoJS from 'crypto-js';
import * as React from 'react';
import * as base64url from 'base64-url';

import {
  ABMapping,
  DefaultValues,
  GlanceRegionPublish,
  PartnerSource,
  RegionPublishMeta,
  StartTimeType,
  UserLevelDefaultValues,
} from './components/GlanceRegionPublish';
import {
  AbsoluteTime,
  DraftState,
  Duration,
  DurationUnit,
  Experiment,
  ExperimentMeta,
  ExperimentVariant,
  GlanceDraft,
  GlanceMeta,
  GlanceTime,
  GlanceType,
  Partner,
  PartnerMeta,
  Region,
  RelativeTime,
  Role,
  SegmentTargetingRule,
  StartTime,
  StoryType,
  StudioAppConfig,
} from 'glance-datatypes';
import {
  BulkChangeData,
  PublishFilter,
  RegionFilter,
  RegionPartnerTuple,
  SortItem,
  TypeFilter,
  CityStates,
} from './types';
import {
  Chip,
  Dialog,
  DropDownMenu,
  FlatButton,
  FloatingActionButton,
  FontIcon,
  MenuItem,
  RadioButton,
  RadioButtonGroup,
  RaisedButton,
  Snackbar,
  TextField,
  Toggle,
} from 'material-ui';
import { GridList, GridTile } from 'material-ui/GridList';
import { Tab, Tabs } from 'material-ui/Tabs';
import {
  Toolbar,
  ToolbarGroup,
  ToolbarSeparator,
  ToolbarTitle,
} from 'material-ui/Toolbar';
import Utils, { allRegions, regionNames, typeFilters } from '../../Utils';

import EmptyMessage from '../../components/common/EmptyMessage';
import GlanceChangeData from '../../components/GlanceChangeData';
import GlanceDraftComponent from './components/GlanceDraft';
import ImageDropzone from '../../components/ImageDropzone';
import JerseyRestApiService from '../../JerseyRestApiService';
import Loader from '../../components/common/Loader';
import { Partners } from '../../Constants';
import RestService from '../../RestService';
import { connect } from 'react-redux';
import {
  warningColor,
  styles,
  selectedColor,
} from './styles/glanceDraftsStyle';

const categoryList = [];
const publisherList = [];

interface GlanceDraftsProps {
  studioAppConfig: StudioAppConfig;
  params: any;
  history?: any;
}

interface State {
  data?: GlanceDraft[];
  originalData?: GlanceDraft[];
  typeFilter: TypeFilter;
  categoryFilter: string;
  publisherFilter: string;
  regionFilter: RegionFilter;
  publishFilter: PublishFilter;
  sortItem: SortItem;
  timeFilter: number;
  link?: string;
  linkWarning?: string;
  snackbarOpen: boolean;
  snackbarMessage?: string;
  partner: Partner;
  region: Region;
  partnerMap: Map<string, Partner>;
  partnerMetaMap: Map<string, PartnerMeta>;
  experiments: Map<string, Experiment>;
  segmentRules: SegmentTargetingRule[];
  selectedPartners: PartnerSource[];
  isPublishSettingChangeClicked: boolean;
  isChangeDataClicked: boolean;
  regionPublishMeta?: Map<Region, RegionPublishMeta>;
  activeRegion: Region;
  errorMsgMap: Map<string, string>;
  draftToRegionPartnerMap: Map<string, RegionPartnerTuple[]>;
  draftIds: string[];
  bulkChangeData?: BulkChangeData;
  limit: number;
  showLoader: boolean;
  currentPage: number;
  totalDrafts: number;
  draftMeta?: Map<string, GlanceMeta[]>;
  bubbleData: {};
  cardType: number;
  testMode: boolean;
  disableCopy: boolean;
  cityStates?: CityStates[];
}

/*
 * GlanceDrafts renders a list of Glance Drafts
 */
class GlanceDrafts extends React.Component<GlanceDraftsProps, State> {
  defaultValues: DefaultValues = {
    durationUnit: DurationUnit.DAYS,
    duration: 2,
    startTimeType: StartTimeType.NOW,
    startDate: new Date(),
    startTime: new Date(),
  };

  userLevelDefaultValues: UserLevelDefaultValues = {
    enabled: true,
    duration: 1,
    durationUnit: DurationUnit.DAYS,
  };

  constructor(props: GlanceDraftsProps) {
    super(props);
    const initialStateFromProp = this.getInitialStateFromProp(props);
    this.state = {
      ...initialStateFromProp,
    };
  }

  getInitialStateFromProp = (props) => {
    let partnerIdToPartner: Map<string, Partner> = new Map<string, Partner>();
    let partnerIdToPartnerMeta: Map<string, PartnerMeta> = new Map<
      string,
      PartnerMeta
    >();
    const { studioAppConfig, params } = props;
    const {
      partners,
      partnerMetas,
      categories,
      segmentTargetingRule: segmentRules,
      publishers,
    } = studioAppConfig;

    partners.forEach((partner) => {
      partnerIdToPartner.set(partner.partnerId, partner);
    });
    partnerMetas.forEach((partner) => {
      partnerIdToPartnerMeta.set(partner.partnerId, partner);
    });
    categoryList.push(
      <MenuItem value={null} key={'null'} primaryText={'All Categories'} />,
    );
    categories.forEach((category) => {
      categoryList.push(
        <MenuItem
          value={category.id}
          key={category.id}
          primaryText={category.displayName}
        />,
      );
    });

    publisherList.push(
      <MenuItem value={null} key={'null'} primaryText={'All Publishers'} />,
    );
    publishers.forEach((publisher) => {
      publisherList.push(
        <MenuItem
          value={publisher.id}
          key={publisher.id}
          primaryText={publisher.displayName}
        />,
      );
    });

    let experiments: Map<string, Experiment> = new Map<string, Experiment>();
    studioAppConfig.experiments.forEach((experiment) => {
      experiments.set(experiment.id, experiment);
    });

    const regionPublishMeta: Map<Region, RegionPublishMeta> = new Map<
      Region,
      RegionPublishMeta
    >();
    const partnerSources: Map<Region, PartnerSource[]> = new Map<
      Region,
      PartnerSource[]
    >();
    const abPartitions: Map<Region, ABMapping[]> = new Map<
      Region,
      ABMapping[]
    >();
    const partnerMap: Map<string, Partner> = new Map<string, Partner>();
    const errorMsgMap: Map<string, string> = new Map<string, string>();
    const draftToRegionPartnerMap: Map<string, RegionPartnerTuple[]> = new Map<
      string,
      RegionPartnerTuple[]
    >();
    const draftMetas: Map<string, GlanceMeta[]> = new Map<
      string,
      GlanceMeta[]
    >();
    studioAppConfig.partners.forEach((partner) => {
      partnerMap.set(partner.partnerId, partner);
    });
    allRegions.forEach((region) => {
      partnerSources.set(region, []);
      abPartitions.set(region, []);
    });

    studioAppConfig.partnerMetas.forEach((item) => {
      const partner = partnerMap.get(item.partnerId);
      item.supportedRegions.forEach((region) => {
        partnerSources.set(region, [
          ...partnerSources.get(region),
          {
            text: partner.name,
            value: partner,
          },
        ]);
      });
      item.abConfigs
        ? item.abConfigs.forEach((abConfig) => {
            abPartitions.set(abConfig.region, [
              ...abPartitions.get(abConfig.region),
              {
                partnerId: partner.partnerId,
                abTag: abConfig.id,
              },
            ]);
          })
        : '';
    });
    allRegions.forEach((region) => {
      regionPublishMeta.set(region, {
        partnerSources: partnerSources.get(region),
        abMappings: abPartitions.get(region),
      });
    });

    const glanceType = params.type ? parseInt(params.type) : GlanceType.STORY;

    const category = params.category
      ? decodeURIComponent(params.category) == 'null'
        ? null
        : decodeURIComponent(params.category)
      : null;

    const publisher = params.publisher
      ? decodeURIComponent(params.publisher) === 'null'
        ? null
        : decodeURIComponent(params.publisher)
      : null;

    const region = params.region ? parseInt(params.region) : null;
    const publishFilter = params.publishState
      ? parseInt(params.publishState)
      : PublishFilter.ALL;
    const timeFilter = params.time ? parseFloat(params.time) : 1;
    const partner = params.partner
      ? this.state.partnerMap.get(params.partner)
      : null;
    const partnerRegion = params.partnerRegion
      ? parseInt(params.partnerRegion)
      : null;
    const draftIds = params.draftIds ? params.draftIds.split(',') : [];

    return {
      snackbarOpen: false,
      snackbarMessage: 'Elves have been sent to fetch some drafts.',
      typeFilter: glanceType,
      categoryFilter: category,
      publisherFilter: publisher,
      publishFilter: publishFilter,
      regionFilter: RegionFilter.ANY,
      partner: partner,
      region: region,
      partnerRegion: partnerRegion,
      sortItem: SortItem.DEFAULT,
      partnerMap: partnerIdToPartner,
      partnerMetaMap: partnerIdToPartnerMeta,
      timeFilter: timeFilter,
      experiments: experiments,
      segmentRules,
      selectedPartners: [],
      isPublishSettingChangeClicked: false,
      isChangeDataClicked: false,
      regionPublishMeta: regionPublishMeta,
      activeRegion: Region.IN,
      errorMsgMap: errorMsgMap,
      draftToRegionPartnerMap: draftToRegionPartnerMap,
      draftIds: draftIds,
      bulkChangeData: null,
      limit: 25,
      currentPage: 1,
      showLoader: true,
      totalDrafts: 0,
      draftMeta: draftMetas,
      bubbleData: {},
      cardType: 1,
      testMode: false,
      disableCopy: false,
    };
  };

  componentDidMount() {
    const { partner, region } = this.state;
    if (partner && region) {
      this.filterDraftsByPartnerRegion(region);
    } else {
      this.loadDrafts(true);
    }
    this.fetchCityStates();
  }

  onEditBulkData = (key: string, value: any) => {
    switch (key) {
      case 'startDay':
      case 'endDay':
        const bulkChangeData = this.state.bulkChangeData || {
          userLifeTimeFilter: null,
        };
        const userLifeTimeFilter = bulkChangeData.userLifeTimeFilter || {};
        this.setState({
          ...this.state,
          bulkChangeData: {
            ...this.state.bulkChangeData,
            userLifeTimeFilter: {
              ...userLifeTimeFilter,
              [key]: value,
            },
          },
        });
        break;
      case 'renderSessionProperties':
      case 'renderSessionMode':
      case 'categoryIds':
      case 'publisherId':
      case 'canSkipSummary':
      case 'shouldUnlock':
      case 'categorySubscriptionStatus':
      case 'targetForCategories':
      case 'advancedCta':
        this.setState({
          ...this.state,
          bulkChangeData: {
            ...this.state.bulkChangeData,
            [key]: value,
          },
        });
        break;
      case 'segmentIds':
        this.setState({
          ...this.state,
          bulkChangeData: {
            ...this.state.bulkChangeData,
            segments: value,
          },
        });
        break;
    }
  };

  loadDraftMetas = (drafts: GlanceDraft[]) => {
    let ids: string[] = [];
    drafts.map((draft) => {
      ids.push(draft.draftId);
    });
    RestService.getDraftToPublishMeta(ids, (draftToMetaMap) => {
      let draftToRegionPartnerMap = this.state.draftToRegionPartnerMap;
      let draftMeta = this.state.draftMeta;
      if (draftToMetaMap != null) {
        for (let k in draftToMetaMap) {
          let tuples = [];
          let metas: GlanceMeta[] = draftToMetaMap[k];
          metas.map((meta) => {
            tuples.push({
              region: meta.region,
              partner: this.state.partnerMap.get(meta.partnerId),
            });
          });
          draftToRegionPartnerMap.set(k, tuples);
          draftMeta.set(k, metas);
        }
      }
      this.setState({
        ...this.state,
        draftToRegionPartnerMap,
        draftMeta,
      });
    });
  };

  durationUpdate = (unit: DurationUnit, value: number) => {
    const { regionPublishMeta } = this.state;
    const currentRegionPublishMeta = regionPublishMeta.get(
      this.state.activeRegion,
    );
    const newCurrentRegionPublishMeta: RegionPublishMeta = {
      ...currentRegionPublishMeta,
      duration: value,
      durationUnit: unit,
    };
    regionPublishMeta.set(this.state.activeRegion, newCurrentRegionPublishMeta);
    this.defaultValues = {
      ...this.defaultValues,
      duration: value,
      durationUnit: unit,
    };
    this.setState({
      ...this.state,
      regionPublishMeta: regionPublishMeta,
    });
  };

  startTimeUpdate = (type: StartTimeType, startDate: Date, startTime: Date) => {
    const { regionPublishMeta } = this.state;
    const currentRegionPublishMeta = regionPublishMeta.get(
      this.state.activeRegion,
    );
    const newCurrentRegionPublishMeta: RegionPublishMeta = {
      ...currentRegionPublishMeta,
      startTimeType: type,
      startDate: startDate,
      startTime: startTime,
    };
    regionPublishMeta.set(this.state.activeRegion, newCurrentRegionPublishMeta);
    this.defaultValues = {
      ...this.defaultValues,
      startTimeType: type,
      startDate: startDate,
      startTime: startTime,
    };
    this.setState({
      ...this.state,
      regionPublishMeta: regionPublishMeta,
    });
  };

  handlerPartnerAdd = (chip: PartnerSource) => {
    const { regionPublishMeta } = this.state;
    let partnerSources = this.state.selectedPartners;
    const currentRegionPublishMeta = regionPublishMeta.get(
      this.state.activeRegion,
    );
    let publishToPartners: PartnerSource[] = currentRegionPublishMeta.publishTo;
    if (!publishToPartners) {
      publishToPartners = [];
    }
    if (partnerSources.indexOf(chip) == -1) {
      partnerSources.push(chip);
      this.setState({
        ...this.state,
        selectedPartners: partnerSources,
      });
    }
    if (
      publishToPartners.indexOf(chip) == -1 &&
      currentRegionPublishMeta.partnerSources.indexOf(chip) != -1
    ) {
      publishToPartners = [...publishToPartners, chip];

      let newCurrentRegionPublishMeta: RegionPublishMeta = {
        ...currentRegionPublishMeta,
        publishTo: publishToPartners,
        publishToSelected: chip.value.partnerId,
      };

      if (
        publishToPartners.length != 1 ||
        !(
          publishToPartners[0].value.partnerId ==
            Partners.SAMSUNG_ANDROID_PARTNER_ID ||
          publishToPartners[0].value.partnerId == Partners.TEST_PARTNER_ID
        )
      ) {
        newCurrentRegionPublishMeta = {
          ...newCurrentRegionPublishMeta,
          userLevelDurationEnabled: false,
          userLevelDuration: undefined,
          userLevelDurationUnit: undefined,
        };
      }

      regionPublishMeta.set(
        this.state.activeRegion,
        newCurrentRegionPublishMeta,
      );
      this.setState({
        ...this.state,
        regionPublishMeta: regionPublishMeta,
      });
    }
  };

  handleCategoryFilterChange = (event: any, index: number, value: string) => {
    this.pushFilterUrl(
      this.state.typeFilter,
      value,
      this.state.publisherFilter,
      this.state.regionFilter,
      this.state.timeFilter,
      this.state.publishFilter,
      this.state.partner,
      this.state.region,
    );
    this.setState(
      {
        ...this.state,
        categoryFilter: value,
      },
      () => {
        const { region, partner } = this.state;
        if (region && partner) {
          this.getPartnerRegionFilteredDrafts(true);
        } else {
          this.loadDrafts(true);
        }
      },
    );
  };

  handlePublisherFilterChange = (event: any, index: number, value: string) => {
    this.pushFilterUrl(
      this.state.typeFilter,
      value,
      this.state.categoryFilter,
      this.state.regionFilter,
      this.state.timeFilter,
      this.state.publishFilter,
      this.state.partner,
      this.state.region,
    );
    this.setState(
      {
        ...this.state,
        publisherFilter: value,
      },
      () => {
        const { region, partner } = this.state;
        if (region && partner) {
          this.getPartnerRegionFilteredDrafts(true);
        } else {
          this.loadDrafts(true);
        }
      },
    );
  };

  handleTypeFilterChange = (event: any, index: number, value: TypeFilter) => {
    this.pushFilterUrl(
      value,
      this.state.categoryFilter,
      this.state.publisherFilter,
      this.state.regionFilter,
      this.state.timeFilter,
      this.state.publishFilter,
      this.state.partner,
      this.state.region,
    );

    this.setState(
      {
        ...this.state,
        typeFilter: value,
      },
      () => {
        const { region, partner } = this.state;
        if (region && partner) {
          this.getPartnerRegionFilteredDrafts(true);
        } else {
          this.loadDrafts(true);
        }
      },
    );
  };

  handleTestModeChange = (event: any, value: boolean) => {
    this.setState({ ...this.state, testMode: value });
  };
  handleRegionFilterChange = (event: any, value: boolean) => {
    const regionFilter = value ? RegionFilter.SPECIFIC : RegionFilter.ANY;
    this.pushFilterUrl(
      this.state.typeFilter,
      this.state.categoryFilter,
      this.state.publisherFilter,
      regionFilter,
      this.state.timeFilter,
      this.state.publishFilter,
      this.state.partner,
      this.state.region,
    );
    this.setState({
      ...this.state,
      regionFilter,
    });
  };

  handlePublishFilterChange = (
    event: any,
    index: number,
    value: PublishFilter,
  ) => {
    this.pushFilterUrl(
      this.state.typeFilter,
      this.state.categoryFilter,
      this.state.publisherFilter,
      null,
      this.state.timeFilter,
      value,
      null,
      null,
    );
    this.setState(
      {
        ...this.state,
        publishFilter: value,
        snackbarMessage: '',
        partner: null,
        region: null,
        data: null,
      },
      () => {
        const { publishFilter } = this.state;

        if (publishFilter === PublishFilter.ALL) {
          this.loadDrafts(true);
        }
      },
    );
  };

  handleSortItemChange = (event: any, index: number, value: SortItem) => {
    const { data } = this.state;
    if (data) {
      if (value === SortItem.PRIORITY_ASC) {
        data.sort((a, b) => a.priority - b.priority);
      } else if (value === SortItem.PRIORITY_DESC) {
        data.sort((a, b) => b.priority - a.priority);
      }
    }

    this.setState({
      ...this.state,
      sortItem: value,
      data,
    });
  };

  handleTimeFilterChange = (event: any, index: number, value: number) => {
    this.pushFilterUrl(
      this.state.typeFilter,
      this.state.categoryFilter,
      this.state.publisherFilter,
      this.state.regionFilter,
      value,
      this.state.publishFilter,
      this.state.partner,
      this.state.region,
    );

    this.setState(
      {
        ...this.state,
        snackbarOpen: true,
        snackbarMessage: 'Elves have been sent to fetch some drafts.',
        timeFilter: value,
        partner: null,
        region: null,
        publishFilter: PublishFilter.ALL,
      },
      () => {
        const { region, partner } = this.state;
        if (region && partner) {
          this.getPartnerRegionFilteredDrafts(true);
        } else {
          this.loadDrafts(true);
        }
      },
    );
  };

  handlePartnerFilterChange = (event: any, index: number, value: string) => {
    let partner: Partner = this.state.partnerMap.get(value);
    this.pushFilterUrl(
      this.state.typeFilter,
      this.state.categoryFilter,
      this.state.publisherFilter,
      this.state.regionFilter,
      this.state.timeFilter,
      this.state.publishFilter,
      partner,
      this.state.region,
    );
    this.setState({
      ...this.state,
      partner,
      region: null,
    });
  };

  filterDraftsByPartnerRegion = (value: Region) => {
    this.pushFilterUrl(
      this.state.typeFilter,
      this.state.categoryFilter,
      this.state.publisherFilter,
      this.state.regionFilter,
      this.state.timeFilter,
      this.state.publishFilter,
      this.state.partner,
      value,
    );
    this.setState(
      {
        ...this.state,
        snackbarOpen: true,
        snackbarMessage: 'Loading ...',
        region: value,
        currentPage: 1,
      },
      () => {
        this.getPartnerRegionFilteredDrafts();
      },
    );
  };

  handlePartnerRegionFilterChange = (
    event: any,
    index: number,
    value: Region,
  ) => {
    this.filterDraftsByPartnerRegion(value);
  };

  onDraftLinkChange = (e: React.FormEvent<{}>, newValue: string) => {
    let warning = '';
    const draftLink = newValue.trim();
    if (
      draftLink.includes('.zip') &&
      !draftLink.startsWith('https://glancecdn.azureedge.net/one-world/')
    ) {
      warning =
        'This webpeek does not belong to Project One World, please migrate to Project One World. Webpeeks built with any other pipeline apart from Project One World will be blocked soon.';
    }
    this.setState({
      ...this.state,
      link: draftLink,
      linkWarning: warning,
    });
  };

  fetchBubbleDataByIds = () => {
    const bubbleIds = this.state.data.map((item) => item.bubbleId);
    const filteredIds = bubbleIds
      .filter((item, index) => {
        if (item) {
          return bubbleIds.indexOf(item) === index;
        }
      })
      .join(',');
    JerseyRestApiService.getBubbleById(filteredIds).then((response) => {
      this.setState({
        ...this.state,
        bubbleData: response,
      });
    });
  };

  fetchCityStates = async () => {
    const response: any = await JerseyRestApiService.getCityStates();
    this.setState({
      ...this.state,
      cityStates: response.cities,
    });
  };

  loadDrafts = (resetPage) => {
    const {
      categoryFilter,
      publisherFilter,
      timeFilter,
      typeFilter,
      limit,
      currentPage,
      draftIds,
    } = this.state;
    const categoryIds = categoryFilter === null ? [] : [categoryFilter];
    const publisherId = publisherFilter === null ? '' : publisherFilter;
    const createdAfterSeconds =
      Math.round(new Date().getTime() / 1000) - timeFilter * 3600;
    const offset = resetPage ? 0 : (currentPage - 1) * limit;
    let type = typeFilter,
      storyType;
    if (typeFilter === TypeFilter.NEWS || typeFilter === TypeFilter.FEATURE) {
      storyType = typeFilters[typeFilter];
      type = TypeFilter.STORY;
    } else if (typeFilter === TypeFilter.GLANCETV) {
      storyType = typeFilters[typeFilter];
      type = TypeFilter.ALL;
    }
    const payload = {
      categoryIds,
      publisherId,
      type: typeFilters[type],
      createdAfterSeconds,
      limit,
      offset,
      storyType,
    };
    this.setState({
      ...this.state,
      showLoader: true,
      data: resetPage ? null : this.state.data,
      currentPage: resetPage ? 1 : currentPage,
    });
    if (!draftIds || draftIds.length == 0) {
      // TODO: Move this api call to actions.

      RestService.getAllDrafts(payload)
        .then((response) => {
          const { drafts, count } = response;

          this.setState({
            ...this.state,
            data: resetPage ? drafts : this.state.data.concat(drafts),
            showLoader: false,
            originalData: drafts,
            totalDrafts: count,
          });
          this.loadDraftMetas(drafts);
          if (drafts.length) {
            this.fetchBubbleDataByIds();
          }
        })
        .catch((error) =>
          this.setState({
            ...this.state,
            snackbarOpen: true,
            showLoader: false,
            snackbarMessage:
              'Something went wrong. Santa denied your request for drafts.',
          }),
        );
    } else {
      // TODO: Move this api call to actions.

      RestService.getDraftsByIds(draftIds, (drafts) => {
        if (drafts != null) {
          this.setState({
            ...this.state,
            data: drafts,
            originalData: drafts,
            showLoader: false,
          });
          this.loadDraftMetas(drafts);
          if (drafts.length) {
            this.fetchBubbleDataByIds();
          }
        } else {
          this.setState({
            ...this.state,
            snackbarOpen: true,
            showLoader: false,
            snackbarMessage:
              'Something went wrong. Santa denied your request for drafts.',
          });
        }
      });
    }
  };

  isDraftPresentInList = (drafts: GlanceDraft[], draft: GlanceDraft) => {
    let result: boolean = false;
    drafts.forEach((item) => {
      if (item.draftId == draft.draftId) {
        result = true;
      }
    });
    return result;
  };

  getPartnerRegionFilteredDrafts = (resetPage = true) => {
    const {
      partner,
      publishFilter,
      region,
      typeFilter,
      categoryFilter,
      publisherFilter,
      timeFilter,
      limit,
      currentPage,
    } = this.state;
    const categoryIds = categoryFilter === null ? [] : [categoryFilter];
    const publisherId = publisherFilter === null ? '' : publisherFilter;
    const createdAfterSeconds =
      Math.round(new Date().getTime() / 1000) - timeFilter * 3600;
    const offset = resetPage ? 0 : currentPage * limit;
    const isPublished = publishFilter === PublishFilter.PUBLISHED;
    let type = typeFilter,
      storyType;
    if (typeFilter === TypeFilter.NEWS || typeFilter === TypeFilter.FEATURE) {
      storyType = typeFilters[typeFilter];

      type = TypeFilter.STORY;
    }
    const payload = {
      categoryIds,
      publisherId,
      type: typeFilters[type],
      createdAfterSeconds,
      limit,
      offset,
      partner,
      region,
      isPublished,
      storyType,
    };

    this.setState(
      {
        ...this.state,
        showLoader: true,
        currentPage: resetPage ? 1 : currentPage,
        data: resetPage ? null : this.state.data,
      },
      () => {
        RestService.getDraftsByPartnerAndRegion(payload)
          .then((response) => {
            const { drafts, count } = response;

            this.setState({
              ...this.state,
              data: resetPage ? drafts : this.state.data.concat(drafts),
              snackbarOpen: false,
              showLoader: false,
              totalDrafts: count,
            });
          })
          .catch((error) => {
            this.setState({
              ...this.state,
              snackbarOpen: false,
              showLoader: false,
            });
          });
      },
    );
  };

  copyDraft = (draft: GlanceDraft) => {
    let copyDraft = { ...draft };
    if (copyDraft.peek.offlinePeekUrl === '') {
      copyDraft.peek.offlinePeekUrl = null;
    }
    copyDraft.createdAtInSecs = Math.round(new Date().getTime() / 1000);

    const getCopyId = async (): Promise<string> => {
      console.log('inside getCopyId');
      let encrypted = CryptoJS.SHA1(
        Math.random().toString() + new Date().getTime.toString(),
      );
      let copyDraftId = base64url
        .encode(String(encrypted), 'utf8')
        .substring(0, 8);
      if (copyDraftId.charAt(0) === 'Y' || copyDraftId.charAt(0) === 'Z') {
        copyDraftId = 'X' + copyDraftId.substring(1, copyDraftId.length);
      }
      const checkId = await RestService.checkDraftById(copyDraftId);
      if (checkId) return copyDraftId;
      getCopyId();
    };

    getCopyId()
      .then((res) => (copyDraft.draftId = res))
      .then(() => {
        this.setState({
          ...this.state,
          showLoader: false,
          disableCopy: false,
          snackbarOpen: false,
        });
      })
      .catch((err) => {
        this.setState({
          ...this.state,
          showLoader: false,
          disableCopy: false,
          snackbarOpen: true,
          snackbarMessage: 'copying draft failed. try again later',
        });
      });

    copyDraft.version = 0;
    copyDraft.editMeta.state = DraftState.NEW;

    let copyAppMeta =
      copyDraft.peek &&
      copyDraft.peek.appCta &&
      copyDraft.peek.appCta.appInstallMeta;
    if (copyAppMeta) {
      let appMatch = this.props.studioAppConfig.apps.filter((app: any) => {
        return app.packageId == copyAppMeta.packageName;
      });
      if (appMatch && appMatch.length) {
        let appItem: any = appMatch[0];
        copyDraft.peek.appCta.appInstallMeta = {
          secondaryBrandColor: appItem.secondaryBrandColor,
          logoUrl: appItem.logoUrl,
          appName: appItem.name,
          category: appItem.category,
          size: appItem.size,
          rating: appItem.rating,
          downloadCount: appItem.downloadCount,
          title: appItem.title,
          description: appItem.description,
          screenshotUrls: appItem.screenshotUrls,
          packageName: appItem.packageId,
          appBundleUrl: '',
          notifTemplateId: '',
          d1NotifTemplateId: '',
          ltvNotifTemplateId: '',
          appId: '',
          brandColor: appItem.brandColor,
          brandBgImage: appItem.brandBgImage,
          appOptionalDetails: appItem.appOptionalDetails,
          preloadEnable: appItem.preloadEnable ? appItem.preloadEnable : false,
          contentProviderUrl: appItem.contentProviderUrl
            ? appItem.contentProviderUrl
            : '',
          prldAttr: appItem.prldAttr,
          templateId: appItem.templateId,
          tag: appItem.tag,
          reviewCount: appItem.reviewCount,
          sizeInMb: appItem.sizeInMb,
          categoryInfo: appItem.categoryInfo,
          bgColor: appItem.bgColor,
          textColor: appItem.textColor,
          carouselImages: appItem.carouselImages,
          installNowCta: appItem.installNowCta,
          installLaterCta: appItem.installLaterCta,
          fbu: appItem.fbu,
          sourceName: appItem.sourceName,
          dspAdvertiserGuid: appItem.dspAdvertiserGuid,
        };
      }
    }

    let newData: GlanceDraft[] = [...this.state.data];
    newData.splice(newData.indexOf(draft) + 1, 0, copyDraft);
    this.setState({
      ...this.state,
      data: newData,
      originalData: newData,
      snackbarOpen: true,
      snackbarMessage: 'Copying..',
      showLoader: true,
      disableCopy: true,
    });
  };

  createDraft = () => {
    if (!this.state.link || this.state.link.length == 0) {
      return;
    }
    let that = this;
    this.setState({
      ...this.state,
      snackbarOpen: true,
      snackbarMessage: 'Hold your horses. Creating new draft...',
    });
    RestService.createDraftFromLink(
      this.state.link,
      this.state.cardType,
      this.state.testMode,
      function(draft: GlanceDraft) {
        if (draft != null) {
          let newData: GlanceDraft[] = [draft, ...that.state.data];
          that.setState({
            ...that.state,
            link: null,
            data: newData,
            originalData: newData,
            snackbarOpen: true,
            snackbarMessage: 'Behold. New draft has been created!',
            typeFilter: TypeFilter.STORY,
          });
        }
      },
    );
  };

  createDraftFromImage = (files: any[]) => {
    let that = this;
    that.setState({
      ...that.state,
      snackbarOpen: true,
      snackbarMessage:
        'Uploading image. Might take a few seconds, go grab a coffee.',
    });
    RestService.uploadImage(files[0], (image) => {
      if (image == null) {
        that.setState({
          ...that.state,
          snackbarOpen: true,
          snackbarMessage: 'Image upload failed.',
        });
      } else {
        that.setState({
          ...that.state,
          snackbarOpen: true,
          snackbarMessage: 'Image uploaded. Creating new draft...',
        });
        RestService.createDraftFromImage(
          image.id,
          this.state.cardType,
          function(draft: GlanceDraft) {
            if (draft != null) {
              let newData: GlanceDraft[] = [draft, ...that.state.data];
              that.setState({
                ...that.state,
                link: null,
                data: newData,
                originalData: newData,
                snackbarOpen: true,
                snackbarMessage: 'Behold. New draft has been created!',
                typeFilter: TypeFilter.WALLPAPER,
              });
            }
          },
        );
      }
    });
  };

  closeSnackbar = () => {
    this.setState({
      ...this.state,
      snackbarOpen: false,
    });
  };

  pushFilterUrl = (
    typeFilter: TypeFilter,
    categoryFilter: string,
    publisherFilter: string,
    regionFilter: RegionFilter,
    timeFilter: number,
    publishFilter: PublishFilter,
    partner: Partner,
    region: Region,
  ) => {
    const url =
      '/' +
      this.props.params.role +
      '/drafts/type/' +
      TypeFilter[typeFilter] +
      '/' +
      typeFilter +
      '/category/' +
      encodeURIComponent(categoryFilter) +
      '/publisher/' +
      encodeURIComponent(publisherFilter) +
      '/time/' +
      timeFilter +
      '/publishState/' +
      PublishFilter[publishFilter] +
      '/' +
      publishFilter +
      (partner ? '/partner/' + partner.partnerId : '') +
      (region ? '/partnerRegion/' + region : '') +
      (regionFilter
        ? '/regionFilter/' + regionFilter
        : partner && region
        ? '/regionFilter/' + RegionFilter.ANY
        : '');
    this.props.history.push(url);
  };

  filterByTypeAndStoryType = (
    glanceType: GlanceType,
    typeFilter: TypeFilter,
    storyType: StoryType,
  ) => {
    if (
      glanceType == GlanceType.WALLPAPER &&
      typeFilter == TypeFilter.WALLPAPER
    ) {
      return true;
    }
    if (
      glanceType == GlanceType.SPONSORED &&
      typeFilter == TypeFilter.SPONSORED
    ) {
      return true;
    }
    if (glanceType == GlanceType.STORY && typeFilter == TypeFilter.STORY) {
      return true;
    }
    if (
      glanceType == GlanceType.STORY &&
      typeFilter == TypeFilter.NEWS &&
      storyType == StoryType.NEWS
    ) {
      return true;
    }
    if (
      glanceType == GlanceType.STORY &&
      typeFilter == TypeFilter.FEATURE &&
      storyType == StoryType.FEATURE
    ) {
      return true;
    }
    return false;
  };

  singleClickPublish = (draft: GlanceDraft) => {
    const { regionPublishMeta, activeRegion } = this.state;
    let errorMsgMap = this.state.errorMsgMap;
    errorMsgMap.set(draft.draftId, '');
    this.setState({
      ...this.state,
      errorMsgMap: errorMsgMap,
    });
    const partnerIds = this.state.selectedPartners.map(
      (partnerSource) => partnerSource.value.partnerId,
    );
    const hasProlongedExpiry =
      draft.type == GlanceType.STORY &&
      !Utils.isExpiryRelaxed(partnerIds) &&
      ((this.defaultValues.durationUnit == DurationUnit.DAYS &&
        this.defaultValues.duration > 30) ||
        (this.defaultValues.durationUnit == DurationUnit.HOURS &&
          this.defaultValues.duration > 720));
    if (hasProlongedExpiry) {
      if (
        draft.frequencyCaps == null ||
        draft.frequencyCaps.weekly == null ||
        draft.frequencyCaps.weekly > 5
      ) {
        errorMsgMap.set(
          draft.draftId,
          'Duration Cannot be more than 72 hours for glances without frequency cap',
        );
        this.setState({
          ...this.state,
          errorMsgMap: errorMsgMap,
        });
        return;
      }
    }
    this.state.selectedPartners.forEach((partner) => {
      if (
        regionPublishMeta &&
        regionPublishMeta.get(activeRegion).partnerSources.indexOf(partner) ==
          -1
      ) {
        errorMsgMap.set(
          draft.draftId,
          'Selected partners and selected region not valid',
        );
        this.setState({
          ...this.state,
          errorMsgMap: errorMsgMap,
        });
        return;
      }
    });
    if (this.state.errorMsgMap.get(draft.draftId)) {
      return;
    }
    if (!this.state.activeRegion) {
      errorMsgMap.set(draft.draftId, 'No region selected in publish settings');
      this.setState({
        ...this.state,
        errorMsgMap: errorMsgMap,
      });
      return;
    }
    if (
      !draft.preferedRegions ||
      draft.preferedRegions.indexOf(this.state.activeRegion) == -1
    ) {
      errorMsgMap.set(
        draft.draftId,
        'Selected region not in preferred regions',
      );
      this.setState({
        ...this.state,
        errorMsgMap: errorMsgMap,
      });
      return;
    }
    if (
      !this.state.selectedPartners ||
      this.state.selectedPartners.length == 0
    ) {
      errorMsgMap.set(draft.draftId, 'No partners selected');
      this.setState({
        ...this.state,
        errorMsgMap: errorMsgMap,
      });
      return;
    }
    let partners: Partner[] = [];
    this.state.selectedPartners.map((partner) => {
      partners.push(partner.value);
    });
    let startTimeObj: StartTime;
    const startTimeType = this.defaultValues.startTimeType;
    if (startTimeType == StartTimeType.NOW) {
      startTimeObj = new StartTime({
        absoluteTime: new AbsoluteTime({
          timeInSecs: Math.round(new Date().getTime() / 1000),
        }),
      });
    } else {
      const startDate = this.defaultValues.startDate;
      const startTime = this.defaultValues.startTime;
      startTimeObj = new StartTime({
        relativeTime: new RelativeTime({
          year: startDate.getFullYear(),
          month: startDate.getMonth() + 1,
          date: startDate.getDate(),
          hour: startTime.getHours(),
          minute: startTime.getMinutes(),
        }),
      });
    }
    const publishMeta = regionPublishMeta.get(this.state.activeRegion);
    if (
      publishMeta &&
      publishMeta.selectedExperiments &&
      publishMeta.selectedExperiments.length > 0 &&
      publishMeta.selectedExperimentMetas.length == 0
    ) {
      errorMsgMap.set(
        draft.draftId,
        'Select at least 1 variant for publishing',
      );
      this.setState({
        ...this.state,
        errorMsgMap: errorMsgMap,
      });
      return;
    }

    const userLevelDuration = this.userLevelDefaultValues.enabled
      ? new Duration({
          unit: this.userLevelDefaultValues.durationUnit,
          length: this.userLevelDefaultValues.duration,
        })
      : null;

    const glanceTime: GlanceTime = new GlanceTime({
      duration: new Duration({
        unit: this.defaultValues.durationUnit,
        length: this.defaultValues.duration,
      }),
      startTime: startTimeObj,
      userLevelDuration: userLevelDuration,
    });
    let resMsg: string = '';

    if (this.state.bulkChangeData != null) {
      if (this.state.bulkChangeData.canSkipSummary != null) {
        if (draft.peek != null) {
          draft.peek.canSkipSummary = this.state.bulkChangeData.canSkipSummary;
        }
      }

      if (this.state.bulkChangeData.categorySubscriptionStatus != null) {
        draft.categorySubscriptionStatus = this.state.bulkChangeData.categorySubscriptionStatus;
      }

      if (
        this.state.bulkChangeData.renderSessionProperties &&
        this.state.bulkChangeData.renderSessionProperties.length
      ) {
        draft.renderSessionProperties = this.state.bulkChangeData.renderSessionProperties;
      }

      if (this.state.bulkChangeData.shouldUnlock != null) {
        if (draft.peek) {
          draft.peek.shouldUnlock = this.state.bulkChangeData.shouldUnlock;
        }
      }

      if (this.state.bulkChangeData.targetForCategories != null) {
        draft.targetForCategories = this.state.bulkChangeData.targetForCategories;
      }

      if (this.state.bulkChangeData.advancedCta != null) {
        draft.peek.loadAndroidJs = this.state.bulkChangeData.advancedCta;
      }

      if (this.state.bulkChangeData.userLifeTimeFilter) {
        draft.userLifeTimeFilter = this.state.bulkChangeData.userLifeTimeFilter;
      }

      if (this.state.bulkChangeData.categoryIds != null) {
        draft.categoryIds = this.state.bulkChangeData.categoryIds;
      }

      if (this.state.bulkChangeData.segments != null) {
        draft.segmentIds = this.state.bulkChangeData.segments.map(
          (seg) => seg.id,
        );
      }
    }
    let that = this;
    if (this.state.bulkChangeData) {
      RestService.saveDraft(draft, function(newDraft: GlanceDraft) {
        if (newDraft) {
          RestService.publishDraft(
            newDraft.draftId,
            that.state.activeRegion,
            partners,
            glanceTime,
            publishMeta.selectedExperimentMetas,
            publishMeta.selectedSegmentRulesList,
            publishMeta.isPartedGlance,
            publishMeta.partingSchedule,
            (result, error) => {
              if (result) {
                resMsg = 'Glance Published Successfully';
              }
              if (error) {
                resMsg = 'Glance Publishing Failed';
              }
              that.updateData(resMsg, draft);
            },
          );
        } else {
          resMsg = 'Glance Publishing Failed';
          this.updateData(resMsg, draft);
        }
      });
    } else {
      RestService.publishDraft(
        draft.draftId,
        that.state.activeRegion,
        partners,
        glanceTime,
        publishMeta.selectedExperimentMetas,
        publishMeta.selectedSegmentRulesList,
        publishMeta.isPartedGlance,
        publishMeta.partingSchedule,
        (result, error) => {
          if (result) {
            resMsg = 'Glance Published Successfully';
          }
          if (error) {
            resMsg = 'Glance Publishing Failed';
          }
          that.updateData(resMsg, draft);
        },
      );
    }
  };

  updateData = (msg: string, draft: GlanceDraft) => {
    let that = this;
    const regionPartnerList = [];
    let draftMeta = this.state.draftMeta;
    RestService.getPublishMeta(draft.draftId, (metas: GlanceMeta[]) => {
      if (metas && metas.length > 0) {
        metas.forEach((meta) => {
          let tuple: RegionPartnerTuple = {
            region: meta.region,
            partner: this.state.partnerMap.get(meta.partnerId),
          };
          regionPartnerList.push(tuple);
        });
      }
      const draftToRegionPartnerMap = this.state.draftToRegionPartnerMap;
      const errorMsgMap = this.state.errorMsgMap;
      errorMsgMap.set(draft.draftId, msg);
      draftToRegionPartnerMap.set(draft.draftId, regionPartnerList);
      draftMeta.set(draft.draftId, metas);
      that.setState({
        ...this.state,
        draftToRegionPartnerMap: draftToRegionPartnerMap,
        errorMsgMap: errorMsgMap,
        draftMeta,
      });
    });
  };

  handleActiveRegion = (region: Region) => {
    this.setState({
      ...this.state,
      activeRegion: region,
    });
  };

  handlerPartnerDelete = (chip: Partner) => {
    const { regionPublishMeta } = this.state;
    let partnerSources: PartnerSource[] = [];
    this.state.selectedPartners.map((partner) => {
      if (chip.partnerId != partner.value.partnerId) {
        partnerSources.push(partner);
      }
    });
    const currentRegionPublishMeta = regionPublishMeta.get(
      this.state.activeRegion,
    );
    if (!currentRegionPublishMeta.publishTo) {
      return;
    }
    const publishToPartners = currentRegionPublishMeta.publishTo.filter(
      (item) => {
        return item.value.partnerId != chip.partnerId;
      },
    );
    const newCurrentRegionPublishMeta: RegionPublishMeta = {
      ...currentRegionPublishMeta,
      publishTo: publishToPartners,
      publishToSelected: null,
    };
    regionPublishMeta.set(this.state.activeRegion, newCurrentRegionPublishMeta);
    this.setState({
      ...this.state,
      regionPublishMeta: regionPublishMeta,
      selectedPartners: partnerSources,
    });
  };

  closePublishDialog = () => {
    this.setState({
      ...this.state,
      isPublishSettingChangeClicked: false,
    });
  };

  closeChangeDataDialog = () => {
    this.setState({
      ...this.state,
      bulkChangeData: null,
      isChangeDataClicked: false,
    });
  };

  saveChangeDataDialog = () => {
    this.setState({
      ...this.state,
      isChangeDataClicked: false,
    });
  };

  handleExperimentSelected = (experimentId: string) => {
    const { regionPublishMeta } = this.state;
    let currentRegionPublishMeta = regionPublishMeta.get(
      this.state.activeRegion,
    );

    let selectedExperimentMeta: ExperimentMeta = null;
    if (currentRegionPublishMeta.selectedExperiments) {
      selectedExperimentMeta = currentRegionPublishMeta.selectedExperiments[0];
    }

    let experiments = [];
    if (experimentId) {
      const selectedExperiment = this.state.experiments.get(experimentId);
      experiments = [
        {
          ...selectedExperimentMeta,
          id: experimentId,
          variantId: selectedExperimentMeta
            ? selectedExperimentMeta.variantId
            : selectedExperiment.control.id,
        },
      ];
    }

    currentRegionPublishMeta = {
      ...currentRegionPublishMeta,
      selectedExperiments: experiments,
      selectedExperimentMetas: [...experiments],
    };
    regionPublishMeta.set(this.state.activeRegion, currentRegionPublishMeta);
    this.setState({
      ...this.state,
      regionPublishMeta: regionPublishMeta,
    });
  };

  handleExperimentVariantSelected = (
    variantId: string,
    experimentId: string,
  ) => {
    const { regionPublishMeta } = this.state;
    let currentRegionPublishMeta = regionPublishMeta.get(
      this.state.activeRegion,
    );
    let selectedExperiment: ExperimentMeta = null;
    let selectedExperimentMetas =
      currentRegionPublishMeta.selectedExperimentMetas;
    selectedExperiment = {
      id: experimentId,
      variantId: variantId,
    };

    const alreadyExists = currentRegionPublishMeta.selectedExperimentMetas.indexOf(
      selectedExperiment,
    );

    if (alreadyExists == -1) {
      selectedExperimentMetas.push(selectedExperiment);
    }

    selectedExperiment = {
      ...selectedExperiment,
      variantId: variantId,
    };

    currentRegionPublishMeta = {
      ...currentRegionPublishMeta,
      selectedExperimentMetas: selectedExperimentMetas,
    };

    regionPublishMeta.set(this.state.activeRegion, currentRegionPublishMeta);
    this.setState({
      ...this.state,
      regionPublishMeta: regionPublishMeta,
    });
  };

  handleExperimentVariantDelete = (
    chip: ExperimentVariant,
    experimentId: String,
  ) => {
    const { regionPublishMeta } = this.state;
    let currentRegionPublishMeta = regionPublishMeta.get(
      this.state.activeRegion,
    );
    const selectedExperiments = currentRegionPublishMeta.selectedExperimentMetas.filter(
      (item) => {
        return item.variantId != chip.id;
      },
    );

    currentRegionPublishMeta = {
      ...currentRegionPublishMeta,
      selectedExperimentMetas: selectedExperiments,
    };

    regionPublishMeta.set(this.state.activeRegion, currentRegionPublishMeta);
    this.setState({
      ...this.state,
      regionPublishMeta: regionPublishMeta,
    });
  };

  getBulkPublishDateAndTime = () => {
    const date = this.defaultValues.startDate;
    const time = this.defaultValues.startTime;
    return (
      date.getDate().toString() +
      '-' +
      (date.getMonth() + 1).toString() +
      '-' +
      date.getFullYear().toString() +
      '; ' +
      time.getHours().toString() +
      ':' +
      time.getMinutes()
    );
  };

  onUserLevelDurationUpdate = (
    enabled: boolean,
    unit?: DurationUnit,
    value?: number,
  ) => {
    const { regionPublishMeta } = this.state;
    const currentRegionPublishMeta = regionPublishMeta.get(
      this.state.activeRegion,
    );
    if (!enabled) {
      unit = null;
      value = null;
    }
    this.userLevelDefaultValues = {
      enabled: enabled,
      duration: value,
      durationUnit: unit,
    };
    const newCurrentRegionPublishMeta: RegionPublishMeta = {
      ...currentRegionPublishMeta,
      userLevelDuration: value,
      userLevelDurationUnit: unit,
      userLevelDurationEnabled: enabled,
    };
    regionPublishMeta.set(this.state.activeRegion, newCurrentRegionPublishMeta);
    this.setState({
      ...this.state,
      regionPublishMeta: regionPublishMeta,
    });
  };

  onPartingTimeUpdate = (enabled: boolean, schedule: any) => {
    const { regionPublishMeta, activeRegion } = this.state;
    const currentRegionPublishMeta = regionPublishMeta.get(activeRegion);
    if (!enabled) {
      schedule = null;
    }
    const newPublishMeta: RegionPublishMeta = {
      ...currentRegionPublishMeta,
      partingSchedule: schedule,
      isPartedGlance: enabled,
    };
    regionPublishMeta.set(activeRegion, newPublishMeta);
    this.setState({
      ...this.state,
      regionPublishMeta: regionPublishMeta,
    });
  };

  getBulkUserLevelDuration = () => {
    const { regionPublishMeta } = this.state;
    let publishMeta = regionPublishMeta.get(this.state.activeRegion);
    if (publishMeta) {
      return publishMeta.userLevelDurationEnabled
        ? 'user: ' +
            publishMeta.userLevelDuration +
            ' ' +
            (publishMeta.userLevelDurationUnit == DurationUnit.DAYS
              ? 'days'
              : 'hours')
        : 'User level expiry disabled';
    }
    return '';
  };

  onPageChange = (page) => {
    this.setState(
      {
        ...this.state,
        currentPage: page,
      },
      () => {
        const { region, partner } = this.state;
        if (region && partner) {
          this.getPartnerRegionFilteredDrafts(false);
        } else {
          this.loadDrafts(false);
        }
      },
    );
  };

  onTargetingRuleDeleted = (index) => {
    const regionPublishMeta = this.state.regionPublishMeta;
    let currentRegionPublishMeta = regionPublishMeta.get(
      this.state.activeRegion,
    );
    let {
      selectedSegmentRulesList: selectedSegmentRulesNew = [],
    } = currentRegionPublishMeta;
    selectedSegmentRulesNew.splice(index, 1);
    currentRegionPublishMeta = {
      ...currentRegionPublishMeta,
      selectedSegmentRulesList: selectedSegmentRulesNew,
    };
    regionPublishMeta.set(this.state.activeRegion, currentRegionPublishMeta);
    this.setState({
      ...this.state,
      regionPublishMeta: regionPublishMeta,
    });
  };

  onSegmentRuleSelected = (value, isAdd, index) => {
    const regionPublishMeta = this.state.regionPublishMeta;
    let currentRegionPublishMeta = regionPublishMeta.get(
      this.state.activeRegion,
    );
    let {
      selectedSegmentRulesList: selectedSegmentRulesNew = [],
    } = currentRegionPublishMeta;
    if (isAdd) {
      if (selectedSegmentRulesNew[index]) {
        selectedSegmentRulesNew[index].push(value);
      } else {
        selectedSegmentRulesNew[index] = [value];
      }
    } else {
      selectedSegmentRulesNew[index] = selectedSegmentRulesNew[index].filter(
        (selSeg) => selSeg.ruleId !== value,
      );
    }
    currentRegionPublishMeta = {
      ...currentRegionPublishMeta,
      selectedSegmentRulesList: selectedSegmentRulesNew,
    };
    regionPublishMeta.set(this.state.activeRegion, currentRegionPublishMeta);
    this.setState({
      ...this.state,
      regionPublishMeta: regionPublishMeta,
    });
  };

  render() {
    let activeRegion: Region = this.state.activeRegion;
    const tabs = [];
    const publishActions = [];
    const changeDataActions = [];
    const {
      limit,
      currentPage,
      data,
      totalDrafts,
      regionPublishMeta,
    } = this.state;

    changeDataActions.push(
      <FlatButton
        label="Turn Off"
        primary={true}
        onClick={this.closeChangeDataDialog}
      />,
    );

    changeDataActions.push(
      <FlatButton
        label="Turn On"
        primary={true}
        onClick={this.saveChangeDataDialog}
      />,
    );

    publishActions.push(
      <FlatButton
        label="Ok"
        primary={true}
        onClick={this.closePublishDialog}
      />,
    );
    allRegions.forEach((region: Region) => {
      tabs.push(
        <Tab
          key={region}
          label={<span key={region}>{regionNames[region]}</span>}
          value={region}
          style={styles.regionTab}
        >
          <GlanceRegionPublish
            key={region}
            region={region}
            regionPublishMeta={regionPublishMeta.get(region)}
            defaultValues={this.defaultValues}
            userLevelDefaultValues={this.userLevelDefaultValues}
            partnerAdded={this.handlerPartnerAdd}
            partnerRemoved={this.handlerPartnerDelete}
            experimentAdded={this.handleExperimentSelected}
            variantSelected={this.handleExperimentVariantSelected}
            variantRemoved={this.handleExperimentVariantDelete}
            onDurationUpdate={this.durationUpdate}
            onStartTimeUpdate={this.startTimeUpdate}
            onUserLevelDurationUpdate={this.onUserLevelDurationUpdate}
            onPartingTimeUpdate={this.onPartingTimeUpdate}
            shortTitleCheck={true}
            cheesySummaryCheck={true}
            partnerMetas={this.props.studioAppConfig.partnerMetas}
            draft={null}
            experiments={this.state.experiments}
            segmentRules={this.state.segmentRules}
            onSegmentRuleSelected={this.onSegmentRuleSelected}
            onTargetingRuleDeleted={this.onTargetingRuleDeleted}
            isSettingsView={true}
            isDspCampaign={false}
          />
        </Tab>,
      );
    });

    let selectedExperiments = [];
    if (
      regionPublishMeta &&
      regionPublishMeta.get(this.state.activeRegion) &&
      regionPublishMeta.get(this.state.activeRegion).selectedExperimentMetas
    ) {
      regionPublishMeta
        .get(this.state.activeRegion)
        .selectedExperimentMetas.map((ex) => {
          let experiment = this.state.experiments.get(ex.id);
          let variant =
            experiment.control.id == ex.variantId ? experiment.control : null;
          variant = variant
            ? variant
            : experiment.variants.find((v) => v.id == ex.variantId);
          selectedExperiments.push({
            experiment: experiment.name,
            variant: variant ? variant.name : 'No Variant',
          });
        });
    }

    const handleCardTypeChange = (_event, value) => {
      this.setState({
        ...this.state,
        cardType: value,
      });
    };

    const { link, linkWarning } = this.state;

    return (
      <div style={{ display: 'inline-block', minWidth: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <RadioButtonGroup
            name="cardType"
            style={{
              display: 'inline-flex',
              width: '25%',
              margin: '25px 0 15px 60px',
            }}
            defaultSelected="1"
            onChange={handleCardTypeChange}
          >
            <RadioButton
              style={{
                float: 'left',
                marginLeft: '5px',
                display: 'inline-block',
              }}
              value="1"
              label="Smart Phone"
            />
            <RadioButton
              style={{
                float: 'left',
                marginLeft: '10px',
                display: 'inline-block',
              }}
              value="2"
              label="TV"
            />
          </RadioButtonGroup>
          <Toggle
            label={'Test Mode'}
            style={{ width: 125, marginTop: 27 }}
            onToggle={(e, value) => this.handleTestModeChange(e, value)}
          />
        </div>
        <div style={{ display: 'block' }}>
          {this.props.studioAppConfig.currentToken.role == Role.ADMIN ||
          this.props.studioAppConfig.currentToken.role == Role.CURATOR ||
          this.props.studioAppConfig.currentToken.role == Role.MODERATOR ? (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                this.createDraft();
              }}
              style={styles.draftLinkForm}
            >
              <TextField
                hintText="Enter a link to create draft"
                style={styles.draftLinkText}
                value={this.state.link == null ? '' : this.state.link}
                inputStyle={{ width: '100%' }}
                onChange={this.onDraftLinkChange}
              />
              {linkWarning && (
                <div style={styles.alertMessage}>
                  <FontIcon
                    className="material-icons"
                    color={warningColor}
                    hoverColor={warningColor}
                    style={{ fontSize: '24px', marginRight: '8px' }}
                  >
                    info
                  </FontIcon>
                  {this.state.linkWarning}
                </div>
              )}
              <div style={styles.draftCTA}>
                <FloatingActionButton mini={true} type="submit">
                  <FontIcon className="material-icons">add</FontIcon>
                </FloatingActionButton>
              </div>
            </form>
          ) : (
            ''
          )}
          {this.props.studioAppConfig.currentToken.role == Role.ADMIN ||
          this.props.studioAppConfig.currentToken.role == Role.DESIGNER ||
          this.props.studioAppConfig.currentToken.role == Role.MODERATOR ? (
            <div style={{ width: '30%', margin: 'auto' }}>
              <ImageDropzone
                draftImageHeight={true}
                onDrop={this.createDraftFromImage}
                style={{ paddingBottom: null }}
              />
            </div>
          ) : (
            ''
          )}
        </div>

        <Toolbar style={{ marginTop: '2%' }}>
          <ToolbarGroup firstChild={true}>
            <ToolbarTitle text="Filters" style={{ marginLeft: '24px' }} />
            <ToolbarSeparator />
            <DropDownMenu
              value={this.state.typeFilter}
              onChange={this.handleTypeFilterChange}
            >
              <MenuItem value={TypeFilter.ALL} primaryText="Everything" />
              <MenuItem value={TypeFilter.WALLPAPER} primaryText="Wallpapers" />
              <MenuItem value={TypeFilter.STORY} primaryText="Stories" />
              <MenuItem value={TypeFilter.SPONSORED} primaryText="Sponsored" />
              <MenuItem value={TypeFilter.NEWS} primaryText="News Stories" />
              <MenuItem
                value={TypeFilter.FEATURE}
                primaryText="Feature Stories"
              />
              <MenuItem value={TypeFilter.GLANCETV} primaryText="Glance TV" />
            </DropDownMenu>
            <DropDownMenu
              maxHeight={400}
              value={this.state.categoryFilter}
              onChange={this.handleCategoryFilterChange}
            >
              {categoryList}
            </DropDownMenu>
            <DropDownMenu
              maxHeight={400}
              value={this.state.publisherFilter}
              onChange={this.handlePublisherFilterChange}
            >
              {publisherList}
            </DropDownMenu>
            <DropDownMenu
              value={this.state.timeFilter}
              onChange={this.handleTimeFilterChange}
            >
              <MenuItem value={24} primaryText="Created Today" />
              <MenuItem value={1} primaryText="1 Hour Ago" />
              <MenuItem value={3} primaryText="3 Hours Ago" />
              <MenuItem value={6} primaryText="6 Hours Ago" />
              <MenuItem value={12} primaryText="12 Hours Ago" />
              <MenuItem value={48} primaryText="Last 2 days" />
              <MenuItem value={72} primaryText="Last 3 days" />
              <MenuItem value={120} primaryText="Last 5 days" />
              <MenuItem value={192} primaryText="Last 8 days" />
              <MenuItem value={360} primaryText="Last 15 days" />
              <MenuItem value={720} primaryText="Last 30 days" />
              <MenuItem value={1440} primaryText="Last 60 days" />
            </DropDownMenu>
            <DropDownMenu
              value={this.state.publishFilter}
              onChange={this.handlePublishFilterChange}
            >
              <MenuItem value={PublishFilter.ALL} primaryText={'All Drafts'} />
              <MenuItem
                value={PublishFilter.PUBLISHED}
                primaryText={'Published Drafts'}
              />
              <MenuItem
                value={PublishFilter.UNPUBLISHED}
                primaryText={'Unpublished Drafts'}
              />
            </DropDownMenu>
            <DropDownMenu
              value={this.state.sortItem}
              onChange={this.handleSortItemChange}
            >
              <MenuItem
                value={SortItem.PRIORITY_ASC}
                primaryText={'Priority Ascending'}
              />
              <MenuItem
                value={SortItem.PRIORITY_DESC}
                primaryText={'Priority Descending'}
              />
              <MenuItem value={SortItem.DEFAULT} primaryText={'Sort By'} />
            </DropDownMenu>
            {this.state.publishFilter != PublishFilter.ALL ? (
              <DropDownMenu
                value={this.state.partner ? this.state.partner.partnerId : null}
                onChange={this.handlePartnerFilterChange}
              >
                <MenuItem value={null} primaryText={'Select Partner'} />
                {this.props.studioAppConfig.partners.map((item, index) => {
                  return (
                    <MenuItem value={item.partnerId} primaryText={item.name} />
                  );
                })}
              </DropDownMenu>
            ) : (
              ''
            )}
            {this.state.partner &&
            this.state.publishFilter != PublishFilter.ALL ? (
              <DropDownMenu
                value={this.state.region}
                onChange={this.handlePartnerRegionFilterChange}
              >
                <MenuItem value={null} primaryText={'Select Region'} />
                {this.state.partnerMetaMap
                  .get(this.state.partner.partnerId)
                  .supportedRegions.map((item, index) => {
                    return (
                      <MenuItem
                        value={item}
                        primaryText={Utils.getReadableRegionName(item)}
                      />
                    );
                  })}
              </DropDownMenu>
            ) : (
              ''
            )}
            {this.state.partner &&
            this.state.region &&
            this.state.publishFilter != PublishFilter.ALL ? (
              <div>
                <Toggle
                  label={
                    'Tagged for ' +
                    Utils.getReadableRegionName(this.state.region)
                  }
                  style={{ width: 200, marginTop: 10 }}
                  onToggle={this.handleRegionFilterChange}
                />{' '}
              </div>
            ) : (
              ''
            )}
          </ToolbarGroup>
        </Toolbar>
        <Toolbar style={{ ...styles.footer, marginTop: '1%', zIndex: 10 }}>
          <ToolbarGroup firstChild={true}>
            <ToolbarTitle
              text="Publish Settings"
              style={{ marginLeft: '24px' }}
            />
            <ToolbarSeparator />
            {this.state.selectedPartners &&
            this.state.selectedPartners.length > 0 ? (
              this.state.selectedPartners.map((partner) => {
                return (
                  <Chip
                    style={styles.chip}
                    key={'ps_' + partner.value.partnerId}
                    backgroundColor={selectedColor}
                  >
                    {partner.text}
                  </Chip>
                );
              })
            ) : (
              <div style={{ marginLeft: '14px' }}>{'No partner Selected'}</div>
            )}
            <ToolbarSeparator />

            <div style={{ marginLeft: '14px' }}>
              {this.state.activeRegion
                ? Utils.getReadableRegionName(this.state.activeRegion)
                : 'No region'}
            </div>
            <ToolbarSeparator />

            <div style={{ marginLeft: '14px' }}>
              {this.defaultValues.startTimeType == StartTimeType.NOW
                ? 'publish now'
                : this.getBulkPublishDateAndTime()}
            </div>
            <ToolbarSeparator />

            <div style={{ marginLeft: '14px' }}>
              {this.defaultValues.duration +
                ' ' +
                (this.defaultValues.durationUnit == DurationUnit.DAYS
                  ? 'days'
                  : 'hours')}
            </div>
            <ToolbarSeparator />
            <div style={{ marginLeft: '14px' }}>
              {this.getBulkUserLevelDuration()}
            </div>

            <ToolbarSeparator />
            {selectedExperiments.length > 0 ? (
              <div style={{ marginLeft: '14px' }}>
                {selectedExperiments.length + ' experiment variants selected'}
              </div>
            ) : (
              <div style={{ marginLeft: '14px' }}>
                {'No experiments Selected'}
              </div>
            )}
            <FlatButton
              label={'Change'}
              primary={true}
              onClick={() => {
                this.setState({
                  ...this.state,
                  isPublishSettingChangeClicked: true,
                });
              }}
            />
            <FlatButton
              label={'Change-Data'}
              key="changeData"
              primary={true}
              onClick={() => {
                this.setState({
                  ...this.state,
                  isChangeDataClicked: true,
                });
              }}
            />
          </ToolbarGroup>
        </Toolbar>
        {
          <div>
            <Dialog
              modal={false}
              open={this.state.isPublishSettingChangeClicked}
              onRequestClose={this.closePublishDialog}
              actions={publishActions}
              autoScrollBodyContent={true}
              contentStyle={{ maxWidth: '1200px', width: '90%' }}
            >
              {
                <Tabs value={activeRegion} onChange={this.handleActiveRegion}>
                  {tabs}
                </Tabs>
              }
            </Dialog>
          </div>
        }
        {
          <div>
            <Dialog
              modal={false}
              open={this.state.isChangeDataClicked}
              onRequestClose={this.closeChangeDataDialog}
              actions={changeDataActions}
              autoScrollBodyContent={true}
            >
              {
                <GlanceChangeData
                  onEdit={this.onEditBulkData}
                  bulkChangeData={this.state.bulkChangeData}
                />
              }
            </Dialog>
          </div>
        }
        {data &&
          (data.length > 0 ? (
            <GridList
              style={{ width: '100%', margin: '0 auto', marginTop: '2%' }}
              cellHeight="auto"
            >
              {data.map((element, index) => {
                return (
                  <GridTile
                    key={element.draftId}
                    containerElement={
                      <GlanceDraftComponent
                        glanceDraftData={element}
                        draftMeta={this.state.draftMeta.get(element.draftId)}
                        experiments={this.state.experiments}
                        singleClickPublish={this.singleClickPublish}
                        errorMsg={this.state.errorMsgMap.get(element.draftId)}
                        regionPartnerList={this.state.draftToRegionPartnerMap.get(
                          element.draftId,
                        )}
                        role={this.props.params.role}
                        copyDraft={this.copyDraft}
                        disableCopy={this.state.disableCopy}
                        bubbleData={this.state.bubbleData[element.bubbleId]}
                        cardType={this.state.cardType}
                        cityStates={this.state.cityStates}
                      />
                    }
                    cols={2}
                  ></GridTile>
                );
              })}
            </GridList>
          ) : (
            <EmptyMessage message="No drafts for this combination of filters"></EmptyMessage>
          ))}
        <Loader
          show={this.state.showLoader}
          message={this.state.snackbarMessage}
          noHeight
        ></Loader>

        {data && totalDrafts > limit * currentPage && (
          <div style={styles.loadMoreStyleContainer}>
            <RaisedButton
              label="Load More"
              primary={true}
              onTouchTap={() => this.onPageChange(this.state.currentPage + 1)}
            />
          </div>
        )}
        <Snackbar
          open={this.state.snackbarOpen}
          message={this.state.snackbarMessage}
          autoHideDuration={5000}
          onRequestClose={this.closeSnackbar}
        />
      </div>
    );
  }
}

const mapStateToProps = (state, ownProps) => {
  return {
    studioAppConfig: state.studioAppConfig,
    ...ownProps,
  };
};

export default connect(mapStateToProps)(GlanceDrafts);



// WEBPACK FOOTER //
// ./src/pages/drafts/GlanceDrafts.tsx